import {
  SearchableSelect,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  IconTextItem,
  Input,
} from '@/shared/components';
import {
  type AssetOption,
  type CreateCryptoAddressFormValues,
  type NetworkOption,
} from '../model';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { getRequiredLabel } from '@/shared/lib';
import { CryptoAddressField } from '@/entities/crypto-address';

type Props = {
  form: UseFormReturn<CreateCryptoAddressFormValues>;
  assetOptions: AssetOption[];
  networkOptions: NetworkOption[];
  validateCryptoAddress: () => Promise<boolean>;
  validateCryptoAddressDebounced: () => void;
  initialValues?: Partial<CreateCryptoAddressFormValues>;
};

export const CreateCryptoAddressForm = ({
  form,
  assetOptions,
  networkOptions,
  validateCryptoAddress,
  validateCryptoAddressDebounced,
  initialValues,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-[1.6rem]"
        onSubmit={e => e.preventDefault()}>
        <FormField
          control={form.control}
          name="assetCode"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <SearchableSelect<AssetOption>
                  {...field}
                  floatingLabel={getRequiredLabel(
                    t('savedAddresses.cryptoAddress.fields.assetLabel')
                  )}
                  value={field.value}
                  isInvalid={Boolean(form.formState.errors?.assetCode?.message)}
                  onValueChange={value => {
                    field.onChange(value);
                    form.setValue('cryptoAddress', '');
                    form.clearErrors('cryptoAddress');
                  }}
                  searchFilter={(option, search) =>
                    option.code.toLowerCase().indexOf(search.toLowerCase()) !==
                    -1
                  }
                  items={assetOptions}
                  renderItem={asset => (
                    <div className="flex items-center justify-between w-full">
                      <IconTextItem
                        icon={asset.icon}
                        title={asset.code}
                        name={asset.name}
                      />
                    </div>
                  )}
                  disabled={Boolean(initialValues?.assetCode)}
                  renderSelectedValue={value => {
                    const asset = assetOptions.find(
                      asset => asset.value === value
                    );

                    if (!asset) return <></>;

                    return (
                      <div className="flex items-center justify-between w-full">
                        <IconTextItem
                          icon={asset.icon}
                          title={asset.code}
                          variant="compact"
                        />
                      </div>
                    );
                  }}
                />
              </FormControl>
              <FormMessage className="px-[1.6rem]" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="networkName"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <SearchableSelect<NetworkOption>
                  {...field}
                  disabled={
                    Boolean(initialValues?.networkName) ||
                    networkOptions.length <= 1
                  }
                  floatingLabel={getRequiredLabel(
                    t('savedAddresses.cryptoAddress.fields.networkLabel')
                  )}
                  isInvalid={Boolean(
                    form.formState.errors?.networkName?.message
                  )}
                  value={field.value}
                  onValueChange={field.onChange}
                  items={networkOptions}
                  renderItem={network => (
                    <div className="flex items-center justify-between w-full">
                      <IconTextItem title={network.code} name={network.name} />
                    </div>
                  )}
                  renderSelectedValue={value => {
                    const network = networkOptions.find(
                      network => network.value === value
                    );

                    if (!network) return <></>;

                    return (
                      <div className="flex items-center justify-between w-full">
                        <IconTextItem title={network.code} variant="compact" />
                      </div>
                    );
                  }}
                />
              </FormControl>
              <FormMessage className="px-[1.6rem]" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cryptoAddress"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <CryptoAddressField
                  {...field}
                  label={getRequiredLabel(
                    t('savedAddresses.cryptoAddress.fields.cryptoAddressLabel')
                  )}
                  onChange={field.onChange}
                  onValueChange={validateCryptoAddressDebounced}
                  onBlur={validateCryptoAddress}
                  disabled={Boolean(initialValues?.cryptoAddress)}
                />
              </FormControl>
              <FormMessage className="px-[1.6rem]" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="templateName"
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    label={getRequiredLabel(
                      t('savedAddresses.cryptoAddress.fields.templateNameLabel')
                    )}
                    className="p3"
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage className="px-[1.6rem]" />
              </FormItem>
            );
          }}
        />
      </form>
    </Form>
  );
};
